// Lokalna nadpisana wersja dostępności (globalnie dla instance)
// @ts-nocheck

import React, { FC } from 'react';
import classnames from 'classnames';
import { BoxSeam } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';

import { IPrice, IStock, IUnit } from 'api/types';

import styles from 'theme/components/controls/Availability/Availability.module.scss';

type AvailabilityUnit = IUnit & {
  is_main?: boolean;
  isMain?: boolean;
  unit_name?: string;
};

type AvailabilityPrice = IPrice & {
  unit_name?: string;
};

export interface AvailabilitySource {
  product_price_full_info?: { prices?: AvailabilityPrice[] };
  product_price_short_info?: { prices?: AvailabilityPrice[] };
  product_unit_short_info?: { units?: AvailabilityUnit[] };
  product_unit_full_info?: { units?: AvailabilityUnit[] };
  units?: AvailabilityUnit[];
}

interface IProps {
  stock: IStock;
  units?: AvailabilityUnit[];
  source?: AvailabilitySource;
}

const normalizeStockName = (name: IStock['name']): string =>
  typeof name === 'string' ? name.trim().toLowerCase() : '';

const shouldKeepOriginalStockLabel = (stock: IStock): boolean => {
  const stockName = normalizeStockName(stock.name);
  return stockName.includes('brak') || stockName.includes('na zamówienie');
};

const isOnOrder = (stock: IStock): boolean =>
  normalizeStockName(stock.name).includes('na zamówienie');

const isMainUnit = (unit: AvailabilityUnit): boolean =>
  unit?.is_main === true ||
  unit?.isMain === true ||
  Number(unit?.converter) === 1;

const getUnitLabel = (unit: AvailabilityUnit | AvailabilityPrice): string | undefined =>
  unit?.shortcut?.trim() ||
  unit?.name?.trim?.() ||
  unit?.unit_name?.trim();

const getMainUnitLabel = (
  source: AvailabilitySource | undefined,
  unitsProp: AvailabilityUnit[] | undefined
): string | undefined => {
  const prices: AvailabilityPrice[] =
    source?.product_price_full_info?.prices ||
    source?.product_price_short_info?.prices ||
    [];

  if (Array.isArray(prices) && prices.length > 0) {
    const mainPrice = prices.find((price) => price?.is_price_for_main_unit === true) || prices[0];
    const fromPrice = getUnitLabel(mainPrice);
    if (fromPrice) return fromPrice;
  }

  const units: AvailabilityUnit[] =
    unitsProp ||
    source?.product_unit_short_info?.units ||
    source?.product_unit_full_info?.units ||
    source?.units ||
    [];

  if (Array.isArray(units) && units.length > 0) {
    const mainUnit = units.find(isMainUnit) || units[0];
    const fromUnit = getUnitLabel(mainUnit);
    if (fromUnit) return fromUnit;
  }

  return undefined;
};

export const buildAvailabilityLabel = (
  stock: IStock,
  source?: AvailabilitySource,
  units?: AvailabilityUnit[],
  availablePrefix = 'Dostępne'
): string => {
  if (shouldKeepOriginalStockLabel(stock)) return String(stock.name ?? '');

  const unitShortcut = getMainUnitLabel(source, units);
  if (!unitShortcut) return String(stock.name ?? '');

  const stockValue = Number.isFinite(Number(stock.value)) ? Math.trunc(Number(stock.value)) : 0;
  const unitNormalized = unitShortcut.trim().toLowerCase();
  const unitAbbreviation = `${unitNormalized.slice(0, 3)}.`;
  return `${availablePrefix} ${stockValue} ${unitAbbreviation}`;
};

const Availability: FC<IProps> = ({ stock, units, source }) => {
  const { t } = useTranslation();
  const label = buildAvailabilityLabel(stock, source, units, t('Dostępne'));
  const typeClass = isOnOrder(stock) ? styles['on-order'] : styles[stock.type];

  return (
    <div
      className={classnames(styles.componentWrapper, typeClass, 'StylePath-Components-Controls-Availability')}>
      <BoxSeam />
      <span className={styles.label}>{label}</span>
    </div>
  );
};

export default Availability;
